import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Button,
  Drawer,
  Group,
  NumberInput,
  Paper,
  Radio,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconBarcode, IconPlus, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import type { ItemDto } from './types';
import { useCreateItem, useUpdateItem } from './itemsApi';
import { useCategories } from '../categories/categoriesApi';
import { useTaxGroups } from '../taxes/taxesApi';
import { RecipePanel } from '../recipes/RecipePanel';
import { CustomFieldsPanel } from '../../components/CustomFields/CustomFieldsPanel';
import { ImageUploadInput } from '../../components/ImageUploadInput';

interface Props {
  opened: boolean;
  onClose: () => void;
  item: ItemDto | null;
}

interface BarcodeRow {
  barcode: string;
  isPrimary: boolean;
}

interface FormValues {
  sku: string;
  nameAr: string;
  nameEn: string;
  description: string;
  categoryId: string | null;
  taxGroupId: string | null;
  unitOfMeasureCode: string;
  purchaseUomCode: string;
  purchaseUomFactor: number;
  basePrice: number;
  costPrice: number | null;
  itemType: string;
  isTrackedInventory: boolean;
  isActive: boolean;
  imageUrl: string | null;
  barcodes: BarcodeRow[];
}

// Mirrors the ItemType enum (1=Standard, 2=Composite, 3=Weighed, 4=Modifier).
const ITEM_TYPES = ['1', '2', '3', '4', '5'] as const;

// Hardcoded for now — UnitsOfMeasure read endpoint isn't wired into the
// SPA yet. These match the codes seeded by DefaultUnitsOfMeasureSeed.
const UNITS = ['PIECE', 'KG', 'G', 'L', 'ML'];

export function ItemFormDrawer({ opened, onClose, item }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateItem();
  const update = useUpdateItem();
  const categoriesQuery = useCategories();
  const taxGroupsQuery = useTaxGroups();
  const submitting = create.isPending || update.isPending;
  const editing = item !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: {
      sku: '',
      nameAr: '',
      nameEn: '',
      description: '',
      categoryId: null,
      taxGroupId: null,
      unitOfMeasureCode: 'PIECE',
      purchaseUomCode: '',
      purchaseUomFactor: 1,
      basePrice: 0,
      costPrice: null,
      itemType: '1',
      isTrackedInventory: false,
      isActive: true,
      imageUrl: null,
      barcodes: [],
    },
    validate: {
      sku: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      basePrice: (v) => (v >= 0 ? null : t('items.form.priceNegative')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      sku: item?.sku ?? '',
      nameAr: item?.nameAr ?? '',
      nameEn: item?.nameEn ?? '',
      description: item?.description ?? '',
      categoryId: item?.categoryId ?? null,
      taxGroupId: item?.taxGroupId ?? null,
      unitOfMeasureCode: item?.unitOfMeasureCode ?? 'PIECE',
      purchaseUomCode: item?.purchaseUomCode ?? '',
      purchaseUomFactor: item?.purchaseUomFactor ?? 1,
      basePrice: item?.basePrice ?? 0,
      costPrice: item?.costPrice ?? null,
      itemType: String(item?.itemType ?? 1),
      isTrackedInventory: item?.isTrackedInventory ?? false,
      isActive: item?.isActive ?? true,
      imageUrl: item?.imageUrl ?? null,
      barcodes: (item?.barcodes ?? []).map((b) => ({
        barcode: b.barcode,
        isPrimary: b.isPrimary,
      })),
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, item]);

  const categoryOptions = useMemo(
    () =>
      (categoriesQuery.data ?? []).map((c) => ({
        value: c.categoryId,
        label: i18n.language === 'ar' ? c.nameAr : (c.nameEn ?? c.nameAr),
      })),
    [categoriesQuery.data, i18n.language],
  );

  const taxGroupOptions = useMemo(
    () =>
      (taxGroupsQuery.data ?? []).map((g) => ({
        value: g.taxGroupId,
        label: i18n.language === 'ar' ? g.nameAr : (g.nameEn ?? g.nameAr),
      })),
    [taxGroupsQuery.data, i18n.language],
  );

  const addBarcode = () => {
    const next = [...form.values.barcodes];
    next.push({ barcode: '', isPrimary: next.length === 0 });
    form.setFieldValue('barcodes', next);
  };

  const removeBarcode = (index: number) => {
    const next = form.values.barcodes.filter((_, i) => i !== index);
    // If we removed the primary, promote the first remaining barcode.
    if (next.length > 0 && !next.some((b) => b.isPrimary)) {
      next[0]!.isPrimary = true;
    }
    form.setFieldValue('barcodes', next);
  };

  const setPrimary = (index: number) => {
    form.setFieldValue(
      'barcodes',
      form.values.barcodes.map((b, i) => ({ ...b, isPrimary: i === index })),
    );
  };

  const submit = async (values: FormValues) => {
    const barcodes = values.barcodes
      .map((b) => ({ barcode: b.barcode.trim(), isPrimary: b.isPrimary }))
      .filter((b) => b.barcode.length > 0);

    const payload = {
      sku: values.sku.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      description: values.description.trim() || null,
      categoryId: values.categoryId,
      itemType: Number(values.itemType),
      basePrice: values.basePrice,
      costPrice: values.costPrice ?? null,
      taxGroupId: values.taxGroupId,
      unitOfMeasureCode: values.unitOfMeasureCode || null,
      purchaseUomCode: values.purchaseUomCode.trim() || null,
      purchaseUomFactor: values.purchaseUomFactor > 0 ? values.purchaseUomFactor : 1,
      isTrackedInventory: values.isTrackedInventory,
      imageUrl: values.imageUrl,
      barcodes,
    };

    try {
      if (editing) {
        await update.mutateAsync({
          id: item.itemId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('items.form.editTitle') : t('items.form.createTitle')}
      position={drawerPosition}
      size="xl"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <Group grow align="flex-start">
            <TextInput
              label={t('items.form.skuLabel')}
              description={t('items.form.skuHelp')}
              withAsterisk
              placeholder="BRG-001"
              {...form.getInputProps('sku')}
            />
            <Select
              label={t('items.form.itemTypeLabel')}
              data={ITEM_TYPES.map((v) => ({ value: v, label: t(`items.itemType.${v}`) }))}
              value={form.values.itemType}
              onChange={(value) => form.setFieldValue('itemType', value ?? '1')}
            />
          </Group>

          <TextInput
            label={t('items.form.nameArLabel')}
            withAsterisk
            placeholder={t('items.form.nameArPlaceholder')}
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('items.form.nameEnLabel')}
            placeholder={t('items.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <Textarea
            label={t('items.form.descriptionLabel')}
            autosize
            minRows={2}
            maxRows={5}
            {...form.getInputProps('description')}
          />

          <ImageUploadInput
            label={t('items.form.imageLabel')}
            kind="items"
            value={form.values.imageUrl}
            onChange={(url) => form.setFieldValue('imageUrl', url)}
          />

          <Group grow>
            <Select
              label={t('items.form.categoryLabel')}
              placeholder={t('items.form.categoryPlaceholder')}
              data={categoryOptions}
              value={form.values.categoryId}
              onChange={(value) => form.setFieldValue('categoryId', value)}
              clearable
              searchable
              disabled={categoriesQuery.isLoading}
            />
            <Select
              label={t('items.form.taxGroupLabel')}
              placeholder={t('items.form.taxGroupPlaceholder')}
              data={taxGroupOptions}
              value={form.values.taxGroupId}
              onChange={(value) => form.setFieldValue('taxGroupId', value)}
              clearable
              searchable
              disabled={taxGroupsQuery.isLoading}
            />
          </Group>

          <Group grow>
            <NumberInput
              label={t('items.form.basePriceLabel')}
              description={t('items.form.basePriceHelp')}
              decimalScale={2}
              fixedDecimalScale
              min={0}
              step={0.5}
              withAsterisk
              {...form.getInputProps('basePrice')}
            />
            <NumberInput
              label={t('items.form.costPriceLabel')}
              description={t('items.form.costPriceHelp')}
              decimalScale={2}
              fixedDecimalScale
              min={0}
              step={0.5}
              value={form.values.costPrice ?? ''}
              onChange={(v) => form.setFieldValue('costPrice', v === '' ? null : Number(v))}
            />
            <Select
              label={t('items.form.unitLabel')}
              data={UNITS.map((u) => ({ value: u, label: t(`items.unit.${u}`) }))}
              value={form.values.unitOfMeasureCode}
              onChange={(value) => form.setFieldValue('unitOfMeasureCode', value ?? '')}
              allowDeselect={false}
            />
          </Group>

          <Group grow>
            <TextInput
              label={t('items.form.purchaseUomLabel')}
              description={t('items.form.purchaseUomHelp')}
              value={form.values.purchaseUomCode}
              onChange={(e) => form.setFieldValue('purchaseUomCode', e.currentTarget.value)}
              placeholder="CTN"
              dir="ltr"
            />
            <NumberInput
              label={t('items.form.purchaseUomFactorLabel')}
              description={t('items.form.purchaseUomFactorHelp')}
              min={0.001}
              decimalScale={3}
              value={form.values.purchaseUomFactor}
              onChange={(v) => form.setFieldValue('purchaseUomFactor', typeof v === 'number' ? v : 1)}
            />
          </Group>

          <Switch
            label={t('items.form.trackInventoryLabel')}
            description={t('items.form.trackInventoryHelp')}
            checked={form.values.isTrackedInventory}
            onChange={(event) =>
              form.setFieldValue('isTrackedInventory', event.currentTarget.checked)
            }
          />

          {/* Barcodes block */}
          <Paper p="md" radius="sm">
            <Group justify="space-between" mb="sm">
              <div>
                <Text fw={500} c="var(--wheels-ink-strong)">
                  {t('items.form.barcodesTitle')}
                </Text>
                <Text fz="xs" c="var(--wheels-ink-muted)">
                  {t('items.form.barcodesHelp')}
                </Text>
              </div>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} stroke={1.8} />}
                onClick={addBarcode}
              >
                {t('items.form.addBarcode')}
              </Button>
            </Group>

            {form.values.barcodes.length === 0 ? (
              <Group gap="xs" c="var(--wheels-ink-muted)">
                <IconBarcode size={16} stroke={1.6} />
                <Text fz="sm">{t('items.form.noBarcodes')}</Text>
              </Group>
            ) : (
              <Stack gap="xs">
                {form.values.barcodes.map((row, index) => (
                  <Group key={index} gap="xs" wrap="nowrap" align="center">
                    <TextInput
                      placeholder="123456789012"
                      value={row.barcode}
                      onChange={(event) =>
                        form.setFieldValue(`barcodes.${index}.barcode`, event.currentTarget.value)
                      }
                      style={{ flex: 1 }}
                      className="font-numeric"
                    />
                    <Radio
                      checked={row.isPrimary}
                      onChange={() => setPrimary(index)}
                      label={t('items.form.primary')}
                    />
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => removeBarcode(index)}
                      aria-label={t('common.delete')}
                    >
                      <IconTrash size={16} stroke={1.6} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            )}
          </Paper>

          {/* Composite items deduct their recipe at SALE time; standard
              items use the recipe as a production formula (معادلة إنتاج)
              consumed by production orders instead — never both. */}
          {editing && (form.values.itemType === '1' || form.values.itemType === '2') && (
            <RecipePanel itemId={item.itemId} />
          )}

          {editing && <CustomFieldsPanel entityType="Item" entityId={item.itemId} />}

          {editing && (
            <Switch
              label={t('items.form.activeLabel')}
              description={t('items.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
